/**
 * Number fields do not step.
 *
 * Every amount in this app is an `<input type="number">` — for the numeric
 * keyboard on a phone and the validation — with its spinner hidden
 * (`.cf-no-spin` in styles.css). What hiding the spinner did not remove is
 * the keyboard half of it: ArrowUp and ArrowDown in the field still add or
 * take away one step, which on an amount is a cent. Reported from use as a
 * leftover nobody wanted: a reader pressing an arrow to move on found the
 * figure they had typed quietly changed.
 *
 * One listener on the document, in the capture phase so it runs before any
 * field's own handler, for every number field the app has or will draw.
 * Only the plain arrows: with a modifier held they are somebody's shortcut.
 */
export function disableNumberStepping(doc: Document = document): void {
  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'number') event.preventDefault();
  }, true);

  /*
   * The mouse wheel is the other half: Chrome steps a FOCUSED number field
   * when the wheel turns over it (Safari does not). Stopping that alone would
   * also stop the page scrolling under the pointer, so the scroll is handed
   * on — to whatever the field sits in that can scroll, or the page — and
   * only the stepping is lost.
   */
  doc.addEventListener('wheel', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') return;
    if (doc.activeElement !== target) return;
    event.preventDefault();
    scrollerOf(target, doc).scrollBy({ left: event.deltaX, top: event.deltaY });
  }, { capture: true, passive: false });
}

/** The nearest ancestor that can scroll, or the page. */
function scrollerOf(el: HTMLElement, doc: Document): Element | Window {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    const overflow = `${style.overflowY} ${style.overflow}`;
    if (/\b(auto|scroll)\b/.test(overflow) && node.scrollHeight > node.clientHeight) return node;
  }
  return doc.defaultView ?? window;
}
